<template>
  <div class="search-container" :class="{ 'input-focused': isInputFocused }">
    <form
      @submit.prevent="submit"
      @submit="onSubmit"
      class="form search"
      role="search"
      novalidate
    >
      <input
        v-model="searchText"
        :placeholder="inputPlaceholder"
        :aria-label="$t('Search on website')"
        :tabindex="isMobile && !isMenuOpen ? -1 : 0"
        ref="input"
        id="watches-search"
        type="search"
        @input="debouncedAutocomplete"
        @focusin="onInputFocusIn"
        @focusout="onInputFocusOut"
      />
      <button
        v-if="searchText.length > 0"
        :aria-label="$t('Clear input')"
        @click="clearInput"
        type="button"
        class="clear-input"
      >
        <i class="icon-close"></i>
      </button>
      <button :aria-label="$t('Submit')" type="submit" class="search-lens">
        <i class="icon-loupe"></i>
      </button>
    </form>

    <ul
      class="suggestions"
      v-if="searchText.length > 2 && firstRequestComplete"
    >
      <li v-if="searchResultMessage" class="title -small results-count">
        {{ searchResultMessage }}
      </li>

      <li
        v-for="(suggestion, index) in suggestions"
        :key="index"
        class="title -small suggestions__item"
      >
        <button class="suggestions__item" @click="applyFilter(suggestion)">
          <strong>{{ suggestion.name }}</strong>
          <span v-if="suggestion.manufacturer_sku">({{ suggestion.manufacturer_sku }})</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import debounce from 'lodash/debounce'

export default {
  name: 'PLPSearch',
  data() {
    return {
      searchText: '',
      suggestions: [],
      isInputFocused: false,
      firstRequestComplete: false
    }
  },
  created() {
    this.debouncedAutocomplete = debounce(this.autocomplete, 300)
  },
  computed: {
    ...mapGetters('search', ['results', 'length', 'loading']),
    ...mapGetters('category', ['getCurrentCategory', 'getAllCategoryFilters']),
    isMobile() {
      return this.$store.state['zenith-ui'].menu.mobile
    },
    isMenuOpen() {
      return this.$store.state['zenith-ui'].menu.open
    },
    inputPlaceholder() {
      return this.$t('Search by watch name or reference number')
    },
    searchResultMessage() {
      if (this.suggestions.length === 0 || this.loading) {
        return this.$t('Sorry, there is no available product for your search.')
      }

      return `${this.$t('All results for your search')} (${
        this.suggestions.length
      })`
    }
  },
  methods: {
    ...mapActions('search', ['search']),
    onInputFocusIn() {
      this.isInputFocused = true
    },
    onInputFocusOut() {
      this.isInputFocused = false
    },
    onSubmit() {
      if (this.suggestions.length > 0) {
        this.applyFilter(this.suggestions[0])
      }
    },
    clearInput() {
      this.searchText = ''
    },
    applyFilter(suggestion) {
      this.$gtm.trackEvent({
        event: 'filterBy',
        filterOption: 'watch_straps_collection',
        filterType: 'toggle',
        value: suggestion.id
      })

      this.$bus.$emit('filter-changed-category', {
        attribute_code: 'watch_straps_collection',
        id: suggestion.id,
        label: suggestion.name
      })

      this.searchText = ''
    },
    autocomplete() {
      this.searchChange()
    },
    searchChange() {
      if (this.searchText.length > 2) {
        this.suggestions = []
        this.search({
          searchText: this.searchText,
          productOnly: true,
          size: 50,
          additionalFilters: [{ key: 'have_straps', value: { in: [1] } }],
          customScoreConfig: {
            minimum_should_match: encodeURIComponent('100%')
          }
        })
      } else {
        // clear suggestions
        this.suggestions = []
      }
    }
  },
  watch: {
    results(oldValue, newValue) {
      let productsToMap

      if (oldValue && oldValue.products) {
        productsToMap = [...oldValue.products]
      } else {
        productsToMap = [...newValue.products]
      }
      const filteredList = productsToMap
        .filter(product => {
          return (
            product.raw.have_straps
          )
        })
        .map(product => {
          return {
            ...product,
            ...product.raw
          }
        })

      this.suggestions = filteredList

      if (!this.firstRequestComplete) {
        this.firstRequestComplete = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/core';

.search {
  position: relative;
  top: 0;
  left: 0;
  z-index: 20;
  width: 100%;

  input {
    width: 100%;
    padding: 2rem 7rem 2rem 3rem;
    position: relative;
    height: auto;
    z-index: 30;
    border: none;
    background: $light-grey;
    outline-offset: 0;
    transition: $transition-default-fast;
    text-transform: uppercase;
    color: var(--zenith-theme-primary);
    font-size: em(15);
    line-height: em(20, 15);
    letter-spacing: 2px;
    font-weight: 500;

    @include bp(max md) {
      padding-right: 5rem;
    }

    &:autofill,
    &:autofill:hover,
    &:autofill:focus {
      font-weight: 500;
      font-size: em(22);
      letter-spacing: em(1.6, 22);
      box-shadow: 0 0 0 1000px $light-grey inset;
    }
  }

  input[type='search']::-webkit-search-decoration,
  input[type='search']::-webkit-search-cancel-button,
  input[type='search']::-webkit-search-results-button,
  input[type='search']::-webkit-search-results-decoration {
    display: none;
  }
}

.search-lens {
  right: 15px;
}

.clear-input {
  visibility: hidden;
  right: 5rem;

  @include bp(max md) {
    right: 3.5rem;
  }

  i {
    pointer-events: none;
  }
}

.clear-input,
.search-lens {
  position: absolute;
  z-index: 40;
  top: 0;
  bottom: 0;
  margin: 0;
  padding: 0;
  background: none;
  border: none;
  color: var(--zenith-header-search-button);

  i {
    display: block;
    font-size: em(25);

    @include bp(max md) {
      font-size: em(22);
    }
  }
}

.search-container {
  margin: 0 1.5rem 3rem;

  &.input-focused {
    .clear-input {
      visibility: visible;
    }
  }

  @include bp(max md) {
    margin-right: 0;
    margin-left: 0;
  }
}

.suggestions {
  background-color: $light-grey;
  margin: 0.5rem 0;
  padding: 2rem 3rem;
  list-style: none;

  &__item {
    letter-spacing: 0.5px;

    &:not(:last-child) {
      margin-bottom: 1.5rem;
    }

    button {
      border: none;
      background: none;
      padding: 0;
      margin: 0;
      text-align: left;
    }

    span {
      color: $mid-grey;
    }
  }
}

.results-count {
  font-weight: 600;
  margin-bottom: 2rem;
}
</style>
